import { Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, Paper, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import Subheading from "../../components/common/Subheading";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { getMasterCompanyNames } from "../../services/gsuite/sheets";
import { PREFLIGHT_COMPANY_LIMIT, runPreflightChecks } from "../../services/preflightChecks";
import { PLATFORM_NAMES } from "../Auto4Client/constants/platformNames";
import {
  CHECK_DESCRIPTIONS,
  mapPreflightIssueRows,
  PREFLIGHT_ISSUE_COLUMNS,
} from "./constants/preflightConstants";

/**
 * Run the classification pipeline's data checks on their own, so missing geocode or
 * untranslated rows show up here instead of as errored companies mid-batch.
 *
 * @returns {JSX.Element}
 */
function PreflightChecks() {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useAlert();

  const [companyNames, setCompanyNames] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(PLATFORM_NAMES);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // null until the first run — an empty array means "ran, nothing wrong".
  const [results, setResults] = useState(null);

  useEffect(() => {
    const loadCompanyNames = async () => {
      const response = await getMasterCompanyNames();
      if (response.status === 200) {
        const data = await response.json();
        setCompanyNames((data.company_names ?? []).slice().sort());
        return;
      }
      showAlert("error", "Could not load company names.");
    };
    loadCompanyNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (selectedCompanies.length === 0) {
      showAlert("error", "Please select at least one company.");
      return;
    }
    if (selectedCompanies.length > PREFLIGHT_COMPANY_LIMIT) {
      showAlert("error", `Please select at most ${PREFLIGHT_COMPANY_LIMIT} companies.`);
      return;
    }
    if (selectedPlatforms.length === 0) {
      showAlert("error", "Please select at least one platform.");
      return;
    }
    if (startDate >= endDate) {
      showAlert("error", "Start date must be before end date.");
      return;
    }

    setIsSubmitting(true);
    setResults(null);
    showAlert("info", "Running checks...");

    try {
      const response = await runPreflightChecks(
        selectedCompanies,
        selectedPlatforms,
        dayjs(startDate).format("YYYY-MM-DD"),
        dayjs(endDate).format("YYYY-MM-DD"),
      );
      if (!(response instanceof Response)) {
        return; // handleHttpResponse already dealt with it (e.g. 403)
      }

      const body = await response.json();
      if (!response.ok) {
        showAlert("error", body.error || "Could not run checks.");
        return;
      }

      const checked = body.results ?? [];
      setResults(checked);
      const failed = checked.filter((result) => !result.passed);
      if (failed.length === 0) {
        showAlert("success", "All checks passed — this data is ready to classify.");
      } else {
        showAlert("warning", `${failed.length} of ${checked.length} companies have data problems.`);
      }
    } catch {
      showAlert("error", "Could not run checks.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const passedCompanies = (results ?? []).filter((result) => result.passed);
  const issueRows = results ? mapPreflightIssueRows(results) : [];

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        <Grid>
          <Title text={"PREFLIGHT CHECKS"} sx={{ my: 5 }} />
        </Grid>
        <Grid size="grow">
          <Box sx={{ display: "flex", justifyContent: "end", mt: 5, gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Checking..." : "Run Checks"}
            </Button>
            <Button variant="outlined" color="error" onClick={() => navigate("/classification-pipeline")}>
              Cancel
            </Button>
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      <Grid container direction="column" spacing={4} p={3} mx={"10%"}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Subheading text="What this checks" sx={{ mb: 1 }} />
          <Box component="ul" sx={{ color: "text.secondary", fontSize: 14, m: 0, pl: 2.5, "& li": { mb: 0.75 } }}>
            {CHECK_DESCRIPTIONS.map((description) => (
              <li key={description}>{description}</li>
            ))}
          </Box>
          <Box sx={{ color: "text.secondary", fontSize: 14, mt: 1.5 }}>
            The classification pipeline runs these same checks before launching each
            company, so anything that passes here won&apos;t be stopped by them. Reads the
            database only — nothing is started and nothing is written.
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Autocomplete
            multiple
            options={companyNames}
            value={selectedCompanies}
            onChange={(event, value) => setSelectedCompanies(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Companies"
                helperText={`Up to ${PREFLIGHT_COMPANY_LIMIT} at a time.`}
              />
            )}
            fullWidth
          />
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Subheading text="Platforms" sx={{ mb: 2 }} />
          <Box display="flex" flexDirection="column">
            {PLATFORM_NAMES.map((platform) => (
              <FormControlLabel
                key={platform}
                control={
                  <Checkbox
                    checked={selectedPlatforms.includes(platform)}
                    onChange={() => {
                      setSelectedPlatforms((prev) =>
                        prev.includes(platform)
                          ? prev.filter((p) => p !== platform)
                          : [...prev, platform]
                      );
                    }}
                  />
                }
                label={platform}
              />
            ))}
          </Box>
          <Box sx={{ color: "text.secondary", fontSize: 13, mt: 1 }}>
            Platforms decide which checks run: the translation check needs Forum Data, the
            profile check needs Linkedin Facebook Data.
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={startDate} handleVal={setStartDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={endDate} handleVal={setEndDate} />
            </Grid>
          </Grid>
        </Paper>

        {results && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Subheading text="Results" sx={{ mb: 1 }} />
            <Box sx={{ fontSize: 14, mb: 2 }}>
              <Box component="span" sx={{ color: "success.main" }}>
                {passedCompanies.length} ready
              </Box>
              {" · "}
              <Box component="span" sx={{ color: issueRows.length ? "error.main" : "text.secondary" }}>
                {results.length - passedCompanies.length} with issues
              </Box>
              {passedCompanies.length > 0 && (
                <Box sx={{ color: "text.secondary", mt: 1 }}>
                  Ready: {passedCompanies.map((result) => result.company_name).join(", ")}
                </Box>
              )}
            </Box>
            {issueRows.length > 0 && (
              <DataTable columns={PREFLIGHT_ISSUE_COLUMNS} dataRows={issueRows} height={"50vh"} />
            )}
          </Paper>
        )}
      </Grid>
    </Layout>
  );
}

export default PreflightChecks;
